import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/scroll";
import { useLang } from "@/lib/i18n";
import { EASE } from "@/components/landing/Reveal";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?q=85&w=2400&auto=format&fit=crop";

const lineVariants = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.1, ease: EASE, delay: 0.25 + i * 0.16 },
  }),
};

const fadeVariants = {
  hidden: { opacity: 0, y: 24 },
  show: (delay) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: EASE, delay },
  }),
};

export default function Hero({ hero }) {
  const h = {
    badge: "Digital byrå för svenska småföretag",
    heading1: "Ta ditt företag",
    heading2: "till",
    accent: "nästa nivå.",
    subtext:
      "Vi hjälper små företag att bygga en starkare digital närvaro – från varumärke och hemsida till Google och sociala medier.",
    ...(hero || {}),
  };
  const { t, dyn } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative flex min-h-screen flex-col overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Mörk skandinavisk interiör med skogsgröna toner"
          className="h-[120%] w-full scale-105 object-cover"
          loading="eager"
        />
        <div className="absolute inset-0 bg-ink/55" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/80 via-ink/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-ink to-transparent" />
      </motion.div>

      <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-1 flex-col justify-center px-4 pb-24 pt-36 sm:px-6 lg:px-8">
        <motion.div
          variants={fadeVariants}
          initial="hidden"
          animate="show"
          custom={0.1}
          className="mb-8 flex items-center gap-3"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-mint opacity-60" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-mint" />
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.3em] text-fog">
            {dyn(h.badge)}
          </span>
        </motion.div>

        <h1 className="font-display text-4xl font-black leading-[1.05] tracking-tight text-bone sm:text-5xl lg:text-7xl">
          <span className="block overflow-hidden pb-1">
            <motion.span variants={lineVariants} initial="hidden" animate="show" custom={0} className="block">
              {dyn(h.heading1)}
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-2">
            <motion.span variants={lineVariants} initial="hidden" animate="show" custom={1} className="block">
              {dyn(h.heading2)} <span className="text-mint">{dyn(h.accent)}</span>
            </motion.span>
          </span>
        </h1>

        <motion.p
          variants={fadeVariants}
          initial="hidden"
          animate="show"
          custom={0.75}
          className="mt-8 max-w-xl text-base leading-relaxed text-fog sm:text-lg"
        >
          {dyn(h.subtext)}
        </motion.p>

        <motion.div
          variants={fadeVariants}
          initial="hidden"
          animate="show"
          custom={0.95}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollToId("#boka")}
            className="group flex items-center justify-center gap-2 rounded-full bg-mint px-8 py-4 text-sm font-bold text-ink transition-all duration-300 hover:bg-bone"
          >
            {t("nav.bokaLong")}
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollToId("#tjanster")}
            className="flex items-center justify-center gap-2 rounded-full border border-white/20 px-8 py-4 text-sm font-semibold text-bone transition-colors duration-300 hover:border-mint hover:text-mint"
          >
            {t("hero.cta2")}
          </button>
        </motion.div>

        <motion.div
          variants={fadeVariants}
          initial="hidden"
          animate="show"
          custom={1.15}
          className="mt-16 flex flex-wrap items-center gap-x-8 gap-y-3 font-mono text-xs uppercase tracking-[0.2em] text-fog"
        >
          <span>{t("hero.stat1")}</span>
          <span className="h-1 w-1 rounded-full bg-moss" />
          <span>{t("hero.stat2")}</span>
          <span className="h-1 w-1 rounded-full bg-moss" />
          <span>{t("hero.stat3")}</span>
        </motion.div>
      </div>

      <motion.button
        data-testid="hero-scroll-cue"
        style={{ opacity: fade }}
        onClick={() => scrollToId("#problem")}
        className="absolute bottom-8 left-1/2 z-10 hidden -translate-x-1/2 flex-col items-center gap-2 text-fog transition-colors hover:text-mint md:flex"
        aria-label={t("hero.scroll")}
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">{t("hero.scroll")}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.button>
    </section>
  );
}
